import React, { ReactElement } from 'react';
import { Field } from 'react-final-form';
import moment from 'moment';
import { anyPass, isEmpty, isNil } from 'ramda';
import { HouseholdMetadata, IPartner, SelectOption } from '@qwealth/qdata';
import { CalendarEvent } from 'data/data-layer/event';

export const TITLES = [
  'Compatibility Meeting',
  'QLife Meeting',
  'QScore Meeting',
  'QConnect Meeting',
  'Service Model',
  'Personal Service',
  'Other',
  'Practice Management',
];

export const LOCATIONS = ['Office Location', 'Client Location', 'Web Conference', 'Teams'];

export const FIELD_SUBSCRIPTION = { error: true, value: true };

export const ConditionValue = ({ when, field, is, children }): ReactElement => (
  <Field name={when} subscription={FIELD_SUBSCRIPTION}>
    {({ input: { value } }) => (value[field] === is ? children : null)}
  </Field>
);

export const yesterday = moment().subtract(1, 'day');

export const isNilOrEmpty = anyPass([isNil, isEmpty]);

export const isEventReady = (event: CalendarEvent): boolean =>
  !isNilOrEmpty(event.subject) &&
  !isNilOrEmpty(event.start.dateTime) &&
  !isNilOrEmpty(event.end.dateTime) &&
  moment(event.end.dateTime).isAfter(event.start.dateTime);

/**
 * Get a list of locations based on the selected household. If no household is provided, return the default locations
 */
export const getLocations = (household?: HouseholdMetadata | null): Array<string> => {
  if (!household) {
    return LOCATIONS;
  }
  const addresses = [...LOCATIONS];
  const address = household.addresses[0];
  if (address) {
    addresses.push(
      `${address.streetAddress} (${address.city}, ${address.provinceOrState})`,
    );
  }
  return addresses;
};

const officeMap = {
  1: [
    { label: 'ETF - Boardroom', value: 'boardroom@etfcm.com' },
    { label: 'QW - Conference Room', value: 'conferenceroom@qwealth.com' },
  ],
  2: [
    { label: 'EWM - Boardroom', value: 'boardroom@elevatew.com' },
    { label: 'EWM - Meeting Room', value: 'meetingroom@elevatew.com' },
  ],
  4: [
    { label: 'OLWM - Large Boardroom', value: 'largeboardroom@onelifewealth.com' },
    { label: 'OLWM - Small Boardroom', value: 'smallboardroom@onelifewealth.com' },
    { label: 'OLWM - Meeting Room 1', value: 'meetingroom1@onelifewealth.com' },
    { label: 'OLWM - Meeting Room 2', value: 'meetingroom2@onelifewealth.com' },
  ],
};

export const buildOfficeLocations = (partner?: IPartner | null): Array<SelectOption<string>> => {
  let locationList: Array<SelectOption<string>> = [];
  if (partner) {
    const { address, id } = partner;
    if (address?.streetAddress) {
      locationList.push({ label: address?.streetAddress, value: address?.streetAddress });
    }
    const rooms = officeMap[id ?? -1];
    if (rooms) {
      locationList = locationList.concat(rooms);
    } else if (id === 99) {
      const allOptions = Object.values(officeMap).reduce(
        (list, current) => list.concat(current),
        [],
      );
      locationList = locationList.concat(allOptions);
    }
  }
  return locationList;
};
